import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';

import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import {
    AbandonApplicantRequest,
    ApplicantResubmitRequest,
    ApplicationStatusRequest,
    ConfirmApplicantDocRequest,
    GenerateRecoveryLinkRequest,
    InternalNoteRequest,
    postAbandonApplicant,
    postApplicantResubmit,
    postApplicationStatus,
    postConfirmApplicantDoc,
    postGenerateRecoveryLink,
    postInternalNote,
    postRefreshLoadPayStatus,
    postRetryAlloyFactoring,
    postRetryDdd,
    postSyncDocumentsFromAlloy,
} from '../endpoints/applicant';
import {
    CancelInformationRequestRequest,
    NeedsMoreInfoRequest,
    RequestInfoRequest,
    deleteInformationRequest,
    postNeedsMoreInfo,
    postNeedsMoreInfoPreview,
    postRequestInfo,
} from '../endpoints/applicantInformationRequests';
import { postOrpUserProfile } from '../endpoints/slack';

// Only the on-screen queries refetch; returning the promise makes mutateAsync wait for them.
const invalidateApplicantQueries = (
    queryClient: QueryClient,
    applicantId: string,
    queryKeyPrefixes: string[],
) =>
    Promise.all(
        queryKeyPrefixes.map((prefix) =>
            queryClient.invalidateQueries({ queryKey: [prefix, applicantId] }),
        ),
    );

// A LoadPay review posts to Alloy (a deny also pushes every file there), and these three are read live from it.
const LOADPAY_ALLOY_QUERY_PREFIXES = [
    'applicant-tags',
    'applicant-reason-codes',
    'applicant-missing-documents',
];

// Fire-and-forget so a dialog doesn't wait on the Alloy reads; the callers refresh status and activity.
const invalidateLoadPayAlloyQueries = (
    queryClient: QueryClient,
    applicantId: string,
    products: ProductNames[],
) => {
    if (products.includes(ProductNames.LOADPAY)) {
        invalidateApplicantQueries(queryClient, applicantId, LOADPAY_ALLOY_QUERY_PREFIXES).catch(
            () => {},
        );
    }
};

// Resubmit reruns the vendor checks and condition processors synchronously and can change status.
export const useResubmitApplicantMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (request: ApplicantResubmitRequest) => {
            return await postApplicantResubmit(request);
        },
        onSuccess: (_data, { applicantId }) =>
            invalidateApplicantQueries(queryClient, applicantId, [
                'applicant',
                'applicant-activity',
                'applicant-due-diligence',
                'applicant-vendor-status',
                'applicant-conditions',
            ]),
    });
};

// The Alloy results arrive later by webhook; refetching now shows the journeys as pending.
export const useRetryAlloyFactoringMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ applicantId }: { applicantId: string }) => {
            return await postRetryAlloyFactoring(applicantId);
        },
        onSuccess: (_data, { applicantId }) =>
            invalidateApplicantQueries(queryClient, applicantId, ['applicant-vendor-status']),
    });
};

// The FMCSA and carrier calls finish inside the request, so their results are readable on success.
export const useRetryDddMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ applicantId }: { applicantId: string }) => {
            return await postRetryDdd(applicantId);
        },
        onSuccess: (_data, { applicantId }) =>
            invalidateApplicantQueries(queryClient, applicantId, [
                'applicant-due-diligence',
                'applicant-vendor-status',
                'applicant-conditions',
            ]),
    });
};

export const useRefreshStatusLoadPayMutation = () =>
    useMutation({
        mutationFn: async ({ applicantId }: { applicantId: string }) => {
            return await postRefreshLoadPayStatus(applicantId);
        },
    });

export const useSubmitNeedsMoreInfoPreviewMutation = () =>
    useMutation({
        mutationFn: async (request: NeedsMoreInfoRequest) => {
            return await postNeedsMoreInfoPreview(request);
        },
    });

export const useSubmitNeedsMoreInfoMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (request: NeedsMoreInfoRequest) => {
            return await postNeedsMoreInfo(request);
        },
        onSuccess: (_data, { applicantId, products }) =>
            invalidateLoadPayAlloyQueries(queryClient, applicantId, products),
    });
};

export const useSubmitInternalNoteMutation = () =>
    useMutation({
        mutationFn: async (request: InternalNoteRequest) => {
            return await postInternalNote(request);
        },
    });

export const useSubmitApplicationStatusMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (request: ApplicationStatusRequest) => {
            return await postApplicationStatus(request);
        },
        onSuccess: (_data, { applicantId, product }) =>
            invalidateLoadPayAlloyQueries(queryClient, applicantId, [product]),
    });
};

export const useConfirmApplicantDocMutation = () =>
    useMutation({
        mutationFn: async (request: ConfirmApplicantDocRequest) => {
            return await postConfirmApplicantDoc(request);
        },
    });

export const useAbandonApplicantMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (request: AbandonApplicantRequest) => {
            return await postAbandonApplicant(request);
        },
        onSuccess: (_data, { applicantId, product }) =>
            invalidateLoadPayAlloyQueries(queryClient, applicantId, [product]),
    });
};

export const useGenerateRecoveryLinkMutation = () =>
    useMutation({
        mutationFn: async (request: GenerateRecoveryLinkRequest) => {
            return await postGenerateRecoveryLink(request);
        },
    });

export const useSyncDocumentsFromAlloyMutation = () =>
    useMutation({
        mutationFn: async ({ applicantId }: { applicantId: string }) => {
            return await postSyncDocumentsFromAlloy(applicantId);
        },
    });

export const useSubmitRequestInfoMutation = () =>
    useMutation({
        mutationFn: async (request: RequestInfoRequest) => {
            return await postRequestInfo(request);
        },
    });

export const useCancelInformationRequestMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (request: CancelInformationRequestRequest) => {
            return await deleteInformationRequest(request);
        },
        // Fire-and-forget so the drawer doesn't wait on the refetch; invalidateQueries doesn't reject.
        onSuccess: (_data, { applicantId }) => {
            invalidateApplicantQueries(queryClient, applicantId, [
                'applicant-activity',
                'applicant',
            ]).catch(() => {});
        },
    });
};

export const useUpsertOrpUserProfileMutation = () =>
    useMutation({
        mutationFn: async (email: string) => {
            return await postOrpUserProfile(email);
        },
    });
